import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Search,
  ShoppingBag,
  LocalShipping,
  CheckCircle,
  AccessTime,
  Cancel,
} from '@mui/icons-material';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Order, OrderTrackingRequest, OrderStatus } from '../../types';
import PriceDisplay from '../../components/common/PriceDisplay';
import api from '../../services/api';

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  orderNumber: Yup.string()
    .trim()
    .required('Order number is required')
    .min(5, 'Order number must be at least 5 characters'),
});

const OrderTracking: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const initialValues: OrderTrackingRequest = {
    email: '',
    orderNumber: '',
  };

  const handleTrackOrder = async (values: OrderTrackingRequest, { setSubmitting }: FormikHelpers<OrderTrackingRequest>) => {
    setLoading(true);
    setSearched(true);

    try {
      const response = await api.post('/orders/track', {
        email: values.email,
        order_number: values.orderNumber
      });
      setOrder(response.data);
    } catch (error: any) {
      setOrder(null);
      if (error.response?.status === 404) {
        toast.error(t('user.orders.tracking.notFound'));
      } else {
        toast.error(t('user.orders.tracking.error'));
      }
      console.error('Order tracking error:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'PENDING':
        return <AccessTime color="warning" />;
      case 'CONFIRMED':
      case 'PROCESSING':
        return <ShoppingBag color="info" />;
      case 'SHIPPED':
        return <LocalShipping color="primary" />;
      case 'DELIVERED':
        return <CheckCircle color="success" />;
      case 'CANCELLED':
        return <Cancel color="error" />;
      default:
        return <AccessTime />;
    }
  };

  const getStatusColor = (status: OrderStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'CONFIRMED':
      case 'PROCESSING':
        return 'info';
      case 'SHIPPED':
        return 'primary';
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderOrderTimeline = () => {
    if (!order) return null;

    const timelineEvents = [
      { status: 'PENDING', label: t('user.orders.tracking.orderPlaced'), date: order.createdAt },
      { status: 'CONFIRMED', label: t('user.orders.tracking.orderConfirmed'), date: order.updatedAt },
      { status: 'PROCESSING', label: t('user.orders.tracking.processing'), date: order.updatedAt },
      { status: 'SHIPPED', label: t('user.orders.tracking.shipped'), date: order.shippedDate },
      { status: 'DELIVERED', label: t('user.orders.tracking.delivered'), date: order.deliveredDate },
    ];

    const currentStatusIndex = timelineEvents.findIndex(event => event.status === order.status);

    return (
      <Stepper activeStep={currentStatusIndex} orientation="vertical">
        {timelineEvents.map((event, index) => (
          <Step key={event.status} completed={index < currentStatusIndex}>
            <StepLabel 
              icon={getStatusIcon(event.status as OrderStatus)}
              optional={
                event.date && index <= currentStatusIndex && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(event.date).toLocaleDateString()}
                  </Typography>
                )
              }
            >
              <Typography variant="h6">
                {event.label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('user.orders.tracking.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('user.orders.tracking.description')}
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleTrackOrder}
        >
          {({ errors, touched, values, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={t('user.orders.tracking.emailAddress')}
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder="Enter your email address"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={t('user.orders.tracking.orderNumber')}
                    name="orderNumber"
                    value={values.orderNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.orderNumber && Boolean(errors.orderNumber)}
                    helperText={touched.orderNumber && errors.orderNumber}
                    placeholder="e.g., ORD-1234567890-ABCD1234"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<Search />}
                    disabled={loading || isSubmitting}
                  >
                    {loading || isSubmitting ? t('user.orders.tracking.tracking') : t('user.orders.tracking.trackOrder')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>

      {searched && !order && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          {t('user.orders.tracking.notFound')}
        </Alert>
      )}

      {order && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Order #{order.orderNumber}
                  </Typography>
                  <Chip 
                    label={order.status} 
                    color={getStatusColor(order.status)}
                    icon={getStatusIcon(order.status)}
                  />
                </Box>

                <Typography variant="h6" gutterBottom>
                  {t('user.orders.tracking.orderProgress')}
                </Typography>
                {renderOrderTimeline()}

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  {t('user.orders.tracking.orderItems')}
                </Typography>
                {order.items.map((item) => (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 1 
                    }}
                  >
                    <Box>
                      <Typography variant="body1">
                        {item.product.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('user.orders.tracking.quantity')}: {item.quantity}
                      </Typography>
                    </Box>
                    <PriceDisplay 
                      price={item.totalPrice} 
                      variant="body1" 
                    />
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{t('user.orders.tracking.total')}:</Typography>
                  <PriceDisplay 
                    price={order.totalAmount} 
                    variant="h6" 
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('user.orders.tracking.shippingDetails')}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>{t('user.orders.tracking.deliveryAddress')}:</strong><br />
                  {order.shippingAddress.address}<br />
                  {order.shippingAddress.city}, {order.shippingAddress.postalCode}<br />
                  {order.shippingAddress.country}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>{t('user.orders.tracking.orderDate')}:</strong><br />
                  {new Date(order.createdAt!).toLocaleDateString()}
                </Typography>

                {order.notes && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('user.orders.tracking.notes')}:</strong><br />
                    {order.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/shop')}
              >
                {t('user.orders.tracking.continueShopping')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default OrderTracking;